import { Edit, Trash2, Eye } from 'lucide-react';
import { LoanResponse } from '@/types/api.types';
import {
  formatCurrency,
  formatDate,
  formatPercentage,
  getStatusColor,
} from '@/utils/helpers';

interface LoanTableRowProps {
  loan: LoanResponse;
  index: number;
  onView: (loanId: number) => void;
  onEdit: (loanId: number) => void;
  onDelete: (loan: LoanResponse) => void;
  onSelect: (loan: LoanResponse) => void;
}

export const LoanTableRow = ({
  loan,
  index,
  onView,
  onEdit,
  onDelete,
  onSelect,
}: LoanTableRowProps) => {
  return (
    <tr
      key={loan.id}
      className="hover:bg-gray-50"
      data-test-id={`loan-row-${index}`}
    >
      <td
        className="table-cell border-r border-gray-300 font-medium"
        data-test-id={`loan-id-${index}`}
      >
        {loan.rp_system_id}
      </td>
      <td
        className="table-cell border-r border-gray-300"
        data-test-id={`loan-client-loan-number-${index}`}
      >
        {loan.client_loan_number || '—'}
      </td>
      <td
        className="table-cell border-r border-gray-300"
        data-test-id={`loan-name-${index}`}
      >
        <button
          onClick={() => onSelect(loan)}
          className="text-blue-600 hover:text-blue-800 hover:underline text-left"
          data-test-id={`loan-name-button-${index}`}
        >
          {loan.loan_name}
        </button>
      </td>
      <td
        className="table-cell border-r border-gray-300"
        data-test-id={`loan-borrower-${index}`}
      >
        {loan.sponsor_borrower}
      </td>
      <td
        className="table-cell border-r border-gray-300"
        data-test-id={`loan-sector-${index}`}
      >
        {loan.property_sector}
      </td>
      <td
        className="table-cell border-r border-gray-300"
        data-test-id={`loan-status-${index}`}
      >
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(loan.loan_status)}`}
          data-test-id={`loan-status-badge-${index}`}
        >
          {loan.loan_status}
        </span>
      </td>
      <td
        className="table-cell border-r border-gray-300"
        data-test-id={`loan-balance-${index}`}
      >
        {formatCurrency(loan.current_balance, 2, loan.currency || 'USD')}
      </td>
      <td
        className="table-cell border-r border-gray-300"
        data-test-id={`loan-spread-${index}`}
      >
        {formatPercentage(
          Number(
            (loan as any).credit_spread_decimal ?? loan.credit_spread
          ) * 100,
          3
        )}
      </td>
      <td
        className="table-cell border-r border-gray-300"
        data-test-id={`loan-maturity-${index}`}
      >
        {formatDate(
          (loan as any).maturity_date ?? loan.original_maturity_date
        )}
      </td>
      <td
        className="table-cell border-r border-gray-300  "
        data-test-id={`loan-actions-${index}`}
      >
        <div
          className="flex gap-2"
          data-test-id={`loan-actions-container-${index}`}
        >
          <button
            onClick={() => onView(loan.rp_system_id)}
            className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
            title="View loan details"
            data-test-id={`view-loan-button-${index}`}
          >
            <Eye className="h-4 w-4" data-test-id={`view-loan-icon-${index}`} />
          </button>
          <button
            onClick={() => onEdit(loan.rp_system_id)}
            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
            title="Edit loan"
            data-test-id={`edit-loan-button-${index}`}
          >
            <Edit
              className="h-4 w-4"
              data-test-id={`edit-loan-icon-${index}`}
            />
          </button>
          <button
            onClick={() => onDelete(loan)}
            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
            title="Delete loan"
            data-test-id={`delete-loan-button-${index}`}
          >
            <Trash2
              className="h-4 w-4"
              data-test-id={`delete-loan-icon-${index}`}
            />
          </button>
        </div>
      </td>
    </tr>
  );
};
